import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ReportAccessDenied } from '@/pages/reports/components/ReportAccessDenied';
import { ErrorState, LoadingState } from '@/pages/reports/detail/components';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  RecipientsTable,
  RecipientsToolbar,
  SendFooterActions,
  SendReportHeader,
  SendStepper,
  MessageStep,
  ReviewStep,
} from './components';
import { useSendReport } from './hooks';

export const SendReportPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    report,
    loading,
    error,
    accessDenied,
    step,
    partnerLabel,
    reportDate,
    canContinue,
    isSubmitting,
    handleBack,
    handleCancel,
    handleContinue,
    recipients,
    message,
  } = useSendReport();

  if (loading) {
    return <LoadingState message="Carregando destinatários..." />;
  }

  if (accessDenied) {
    return <ReportAccessDenied />;
  }

  if (error || !report) {
    return (
      <ErrorState
        message={error || 'Report não encontrado'}
        onBack={() => navigate('/app/reports')}
      />
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex min-h-[calc(100vh-8rem)] w-full flex-col bg-white">
        <SendReportHeader
          title={report.title}
          reportDate={reportDate}
          partnerLabel={partnerLabel}
          status={report.status}
          onBack={handleBack}
        >
          <SendStepper currentStep={step} />
        </SendReportHeader>

        <div className="flex w-full flex-1 flex-col gap-6 px-6 pt-6">
          {step === 1 ? (
            <>
              <RecipientsToolbar
                selectedCount={recipients.selectedCount}
                search={recipients.search}
                onSearchChange={recipients.setSearch}
                statusFilter={recipients.statusFilter}
                onStatusFilterChange={recipients.setStatusFilter}
              />
              <RecipientsTable
                recipients={recipients.visibleRecipients}
                selectedIds={recipients.selectedIds}
                headerSelectionState={recipients.headerSelectionState}
                sort={recipients.sort}
                hasMore={recipients.hasMore}
                sentinelRef={recipients.sentinelRef}
                scrollRootRef={recipients.scrollRootRef}
                onToggle={recipients.toggleRecipient}
                onToggleSelectAll={recipients.toggleSelectAllFiltered}
                onSort={recipients.toggleSort}
                onLoadMore={recipients.loadMore}
                loading={recipients.loading}
              />
            </>
          ) : null}

          {step === 2 ? (
            <MessageStep
              reportTitle={report.title}
              subject={message.subject}
              subjectError={message.subjectError}
              onSubjectChange={message.onSubjectChange}
              body={message.body}
              bodyError={message.bodyError}
              onBodyChange={message.onBodyChange}
            />
          ) : null}

          {step === 3 ? (
            <div className="flex flex-col gap-[68px]">
              <ReviewStep
                selectedCount={recipients.selectedCount}
                subject={message.subject}
                body={message.body}
              />
              <SendFooterActions
                step={step}
                canContinue={canContinue}
                isSubmitting={isSubmitting}
                onCancel={handleCancel}
                onBackStep={handleBack}
                onContinue={handleContinue}
              />
            </div>
          ) : (
            <div className="mt-auto">
              <SendFooterActions
                step={step}
                canContinue={canContinue}
                isSubmitting={isSubmitting}
                onCancel={handleCancel}
                onBackStep={handleBack}
                onContinue={handleContinue}
              />
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};
